export namespace ORY_POLIS_CONFIG {
    export const DOMAIN = 'federation.funkey.be';
    export const BASE_URL = `https://${DOMAIN}`;

    /** The product identifier for `funkey.be` inside @ory/polis */
    export const PRODUCT_ID = `funkey.be`;

    /** Funkey's own bizz. SSO under this tenant also elevates the user to super-admin; the slug is
     * the trust boundary (customer connections live under their own tenant, never this one). */
    export const ADMIN_TENANT = 'funkey';
}

/** Supported SAML identity providers. Each `value` is the Polis `providerName` (the IdP entityID host),
 * so it doubles as the `mapProfileToUser` union literal and the substring we look for in pasted metadata. */
export const SAML_PROVIDER_VALUES = ['sts.windows.net', 'accounts.google.com'] as const;
export type SamlProviderValue = (typeof SAML_PROVIDER_VALUES)[number];

export const SAML_PROVIDERS = [
    { value: 'sts.windows.net', label: 'Microsoft Azure' },
    { value: 'accounts.google.com', label: 'Google Workspace' },
] as const satisfies readonly { value: SamlProviderValue; label: string }[];

/** The vendor to name on a sign-in button, derived from the IdP's own `entityID`.
 *
 * Null for anything we can't name — a customer's own federation server (NetIQ, Ping, ADFS…) — and the
 * button then falls back to the customer's name and logo. Read from the entityID rather than the
 * stored `provider` on purpose: the provider enum holds only Azure and Google, so a custom IdP has to
 * be filed under one of them, and trusting that choice announces the wrong vendor. */
export function samlSignInVendor(
    entityId: string | null,
    /** Fallback for connections predating `idp_entity_id` — the 0012 backfill left it null and stamped
     * every migrated tenant as Azure, so without this every legacy customer loses its vendor button. */
    provider?: SamlProviderValue | null,
): 'Microsoft' | 'Google' | null {
    if (!entityId) {
        if (provider === 'sts.windows.net') return 'Microsoft';
        if (provider === 'accounts.google.com') return 'Google';
        return null;
    }

    // Matched on the parsed hostname, never as a substring: a customer host like
    // `sso.google.com.acme.example` or a path/query mentioning a vendor would otherwise be branded as
    // that vendor. Anything that isn't a URL (some IdPs use a URN) is a custom IdP by definition.
    const hostname = ((): string | null => {
        try {
            return new URL(entityId).hostname.toLowerCase();
        } catch {
            return null;
        }
    })();
    if (!hostname) return null;

    if (hostname === 'sts.windows.net' || hostname === 'login.microsoftonline.com')
        return 'Microsoft';
    if (hostname === 'accounts.google.com') return 'Google';
    return null;
}

/** Detect the provider from pasted IdP metadata: returns the single provider whose host appears in the
 * XML (entityID), or null when none or more than one match. */
export function detectSamlProvider(xml: string): SamlProviderValue | null {
    const matches = SAML_PROVIDER_VALUES.filter(value => xml.includes(value));
    return matches.length === 1 ? (matches[0] ?? null) : null;
}
